import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../api/auth';
import { getToken, getTokenPayload, isTokenExpired, removeTokens, setToken, setRefreshToken } from '../utils/token';
import { getBackendErrorKey } from '../utils/errorMapper';
import api from '../api/axios';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setTokenState] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Проверка авторизации при загрузке
  useEffect(() => {
    const checkAuth = () => {
      const storedToken = getToken();
      
      if (storedToken && !isTokenExpired(storedToken)) {
        const payload = getTokenPayload(storedToken);
        setTokenState(storedToken);
        setUser(payload);
        setIsAuthenticated(true);
      } else {
        // Токен истек или отсутствует
        if (storedToken) {
          removeTokens();
        }
        setTokenState(null);
        setUser(null);
        setIsAuthenticated(false);
      }
      
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Вход в систему
  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      
      if (response.accessToken) {
        const payload = getTokenPayload(response.accessToken);
        setTokenState(response.accessToken);
        setUser(payload);
        setIsAuthenticated(true);
        
        return { success: true, data: response };
      }
      
      return { success: false, errorKey: 'auth.login.errors.loginError' };
    } catch (error) {
      const errorKey = getBackendErrorKey(error);
      return { 
        success: false, 
        errorKey 
      };
    }
  };

  // Обработка OAuth callback
  const handleOAuthCallback = (accessToken, refreshToken, userRole) => {
    try {
      // Сохраняем токены
      setToken(accessToken);
      setRefreshToken(refreshToken);
      
      // Извлекаем данные пользователя из токена
      const payload = getTokenPayload(accessToken);
      setTokenState(accessToken);
      setUser(payload);
      setIsAuthenticated(true);
      
      return { success: true };
    } catch (error) {
      console.error('OAuth callback error:', error);
      return { success: false, error: 'Failed to process OAuth callback' };
    }
  };

  // Регистрация
  const register = async (email, password) => {
    try {
      const response = await authAPI.register(email, password);
      return { success: true, data: response };
    } catch (error) {
      const errorKey = getBackendErrorKey(error);
      return { 
        success: false, 
        errorKey 
      };
    }
  };

  // Выход из системы
  const logout = () => {
    authAPI.logout();
    setTokenState(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  // Обновление информации о пользователе
  const refreshUserInfo = () => {
    const storedToken = getToken();
    
    if (storedToken && !isTokenExpired(storedToken)) {
      const payload = getTokenPayload(storedToken);
      setUser(payload);
      setIsAuthenticated(true);
    } else {
      logout();
    }
  };

  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    refreshUserInfo,
    handleOAuthCallback
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};
